/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SliderComponent } from '@syncfusion/ej2-react-inputs';

type Props = {
  cy: any; // cytoscape.Core | null
  min?: number;
  max?: number;
  step?: number;
  className?: string;
};

const SpacingFactorControl: React.FC<Props> = ({
  cy,
  min = 0.5,
  max = 2.5,
  step = 0.05,
  className = 'inline-flex items-center gap-2 h-11 px-2 rounded-lg border border-gray-300/60 bg-white mt-3'
}) => {
  const [factor, setFactor] = useState<number>(1);
  const basePositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const centerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const canApply = useMemo(() => !!cy && cy?.nodes?.().length > 0, [cy]);

  const captureBaseline = () => {
    if (!cy) return;
    const nodes = cy.nodes();
    const bb = nodes.boundingBox();
    centerRef.current = { x: (bb.x1 + bb.x2) / 2, y: (bb.y1 + bb.y2) / 2 };
    const map: Record<string, { x: number; y: number }> = {};
    nodes.forEach((n: any) => {
      const p = n.position();
      map[n.id()] = { x: p.x, y: p.y };
    });
    basePositionsRef.current = map;
  };

  useEffect(() => {
    if (!cy) return;
    captureBaseline();

    const onLayoutStop = () => {
      setFactor(1);
      captureBaseline();
    };
    cy.on('layoutstop', onLayoutStop);
    return () => {
      cy.off('layoutstop', onLayoutStop);
    };
  }, [cy]);

  const applyFactor = (f: number) => {
    if (!cy) return;
    const base = basePositionsRef.current;
    const center = centerRef.current;

    cy.batch(() => {
      cy.nodes().forEach((n: any) => {
        if (n.locked && n.locked()) return;
        const id = n.id();
        const b = base[id];
        if (!b) return;
        const dx = b.x - center.x;
        const dy = b.y - center.y;
        n.position({ x: center.x + dx * f, y: center.y + dy * f });
      });
    });
  };

  // wird auch vom Zahlenfeld genutzt
  const handleChange = (value: string | number) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return;
    setFactor(v);
    applyFactor(v);
  };

  return (
    <div className={className}>
      <div className="h-10 flex items-center">
        <SliderComponent
          cssClass="sf-spacing-gray sf-compact"
          width={60}               
          min={min}
          max={max}
          step={step}
          value={factor}
          type="MinRange"
          showButtons={false}
          enabled={canApply}
          tooltip={{ isVisible: false }}
          change={(args) => handleChange(args.value)}   
          changed={(args) => handleChange(args.value)} 
          title='Spacing'
        />
      </div>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={factor}
        onChange={(e) => handleChange(e.target.value)}
        disabled={!canApply}
        className="w-16 h-8 px-2 rounded-lg border border-gray-300/60 bg-white"
        title='Spacing'
      />
    </div>
  );
};

export default SpacingFactorControl;
